import { compareDateTimes, formatInputDate } from "./Time";
import { formatSplitKey } from "../commons/FormatValue";

const filteredItemByArray = (data = [], filtered = []) => {
  return data?.filter((item) =>
    filtered.every(({ name, value }) =>
      value ? `${formatSplitKey(name, ".", item)}` === `${value}` : item
    )
  );
};

const filteredItemBySearch = (data = [], search = [], key = "") => {
  return data?.filter((item) =>
    search
      .filter(({ value }) => value)
      .every(({ name, ...search }) =>
        compareDateTimes(item, { ...search, name: key || name })
      )
  );
};

const filteredItemInArray = (data = [], key = "", value = "") => {
  return data?.filter(
    (item) => `${formatSplitKey(key, ".", item)}` === `${value}`
  );
};

const filteredNoneItemInArray = (data = [], key = "", value = "") => {
  return data?.filter(
    (item) => `${formatSplitKey(key, ".", item)}` !== `${value}`
  );
};

const filteredMultiItemInArray = (data = [], key = "", value = []) => {
  return data?.filter((item) =>
    value.includes(`${formatSplitKey(key, ".", item)}`)
  );
};

const filteredNoneMultiItemInArray = (data = [], key = "", value = []) => {
  return data?.filter(
    (item) => !value.includes(`${formatSplitKey(key, ".", item)}`)
  );
};

const filteredCreateNewKey = ({
  data = [],
  search = [],
  newName = "",
  newValue = "",
  duplicateKey = false
}) => {
  return data
    .filter((item) =>
      search.every(({ name, value }) =>
        value ? `${formatSplitKey(name, ".", item)}` === `${value}` : item
      )
    )
    .map((item) => ({
      name: newName,
      value: item?.[newValue || newName],
      duplicateKey
    }));
};

const filteredItemByFindArray = (
  filtered = { data: [], key: "" },
  finded = { data: [], key: "", value: "" }
) => {
  const keyToFind = findItemInArray(finded?.data, finded?.key, finded?.value)?.[
    filtered?.key
  ];
  return filteredItemInArray(filtered?.data, filtered?.key, keyToFind);
};

const filteredItemInRange = (
  data = [],
  search = [],
  startKey = "",
  endKey = ""
) => {
  const [start, end] = search.reduce(
    (dates, { name, value }) => {
      const formatDates = (value, timezone) =>
        value ? new Date(formatInputDate(value, timezone).date) : null;
      if (name === startKey) dates[0] = formatDates(value, "ISO");
      if (name === endKey) dates[1] = formatDates(value, "Local");
      return dates;
    },
    [null, null]
  );

  return data?.filter((item) => {
    const reduceKey = (key) => formatSplitKey(key, ".", item);
    const startDate = new Date(reduceKey(startKey));
    const endDate = new Date(reduceKey(endKey));

    if (start && !end) return startDate >= start;
    else if (!start && end) return endDate <= end;
    else if (start && end) return startDate >= start && endDate <= end;
    else return item;
  });
};

const filteredItemMoreCurrentDate = (data) => {
  const currentDate = new Date();

  return (filteredData = data?.filter((item) => {
    const endDate = new Date(item.Contract_End_Date);
    return endDate > currentDate;
  }));
};

const filteredItemByDateBetween = (
  data = [],
  start = "",
  end = "",
  key1 = "",
  key2 = ""
) => {
  const startDate = new Date(formatInputDate(start, "ISO")?.date);
  const endDate =
    end !== "" ? new Date(formatInputDate(end, "Local")?.date) : null;

  return data.filter((item) => {
    const item1 = key1 ? new Date(formatSplitKey(key1, ".", item)) : null;
    const item2 = key2 ? new Date(formatSplitKey(key2, ".", item)) : null;

    if (!endDate) return item1 >= startDate;
    else if (item2) return item1 >= startDate && item2 <= endDate;
    else return item1 >= startDate && item1 <= endDate;
  });
};

const findItemInArray = (data = [], key = "", value = "") => {
  return data?.find(
    (item) => `${formatSplitKey(key, ".", item)}` === `${value}`
  );
};

const findItemBySearch = (data = [], search = [], key = "") => {
  return data?.find((item) =>
    search.some(
      ({ name, value }) =>
        value && `${formatSplitKey(key || name, ".", item)}` === `${value}`
    )
  );
};

const filteredIndex = (data = [], key = "") => {
  return data?.filter(
    (item, idx, self) =>
      idx === self.findIndex((el) => el?.[key] === item?.[key])
  );
};

const flattenedArray = (data = [], arrKey = "") => {
  return data?.flatMap((item) =>
    item[arrKey]?.length
      ? item[arrKey].map((childArray) => ({ ...item, [arrKey]: childArray }))
      : { ...item }
  );
};

const emptyValue = ["", null, undefined];
const hasValueObjects = (data = [], arrayKey = []) => {
  const emptyKey = [];
  const hasValue = data?.filter((obj) =>
    Object?.keys(obj)?.some(
      (key) => emptyValue.includes(obj?.[key]) && arrayKey?.includes(key)
    )
  );
  if (hasValue?.length > 0) {
    Object?.keys(hasValue?.[0])?.map(
      (key) => emptyValue.includes(hasValue?.[0]?.[key]) && emptyKey.push(key)
    );
  }
  return { hasValue, emptyKey };
};

const emptyValueObjects = (data = [], arrayKey = []) => {
  return data?.filter((obj) =>
    Object?.keys(obj)?.some(
      (key) => emptyValue.includes(obj?.[key]) && !arrayKey.includes(key)
    )
  );
};

const hasDuplicateValues = (array = [], key = []) => {
  return array.some(
    (item, index) =>
      array.findIndex((other, otherIndex) =>
        key.some((k) => `${other[k]}` === `${item[k]}` && otherIndex !== index)
      ) !== -1
  );
};

const concatenatedLabels = (emptyKey, inputField) => {
  return emptyKey.reduce((acc, cur, idx) => {
    const filtered = inputField.filter(({ name }) => cur === name);
    const labels = filtered.map(({ label }) => label).join(", ");
    return acc + labels + (idx < emptyKey.length - 1 ? ", " : "");
  }, "");
};

export {
  filteredItemByArray,
  filteredItemBySearch,
  filteredItemInArray,
  filteredNoneItemInArray,
  filteredMultiItemInArray,
  filteredNoneMultiItemInArray,
  filteredCreateNewKey,
  filteredItemByFindArray,
  filteredItemInRange,
  filteredItemMoreCurrentDate,
  filteredItemByDateBetween,
  findItemInArray,
  findItemBySearch,
  filteredIndex,
  flattenedArray,
  hasValueObjects,
  emptyValueObjects,
  hasDuplicateValues,
  concatenatedLabels
};
